import { CheckoutTreeNode } from "@redotech/redo-model/checkouts/checkout-tree";
import { ManuallyTypedWidgetCheckoutOptimizationConfig } from "@redotech/redo-model/checkouts/widget";
import {
  type WidgetAutocheck,
  type WidgetFees,
} from "@redotech/redo-model/widget";
import {
  createRedoShopifyRpcClient,
  type RedoShopifyRpcClient,
} from "@redotech/shopify-sdk/rpc/client";
import { STORE_URL_HEADER } from "@redotech/shopify-sdk/rpc/custom-http-headers";
import { getCheckoutOptimizationConfig } from "@redotech/shopify-sdk/rpc/schema/checkout-optimization/get-checkout-optimization-config";
import {
  getCheckoutOptimizationCustomer,
  GetCheckoutOptimizationCustomerOutput,
} from "@redotech/shopify-sdk/rpc/schema/checkout-optimization/get-checkout-optimization-customer";
import { getOrderEditingSettings } from "@redotech/shopify-sdk/rpc/schema/get-order-editing-settings";
import { smsOptIn } from "@redotech/shopify-sdk/rpc/schema/sms-opt-in";

export const shopifyRpcClient: RedoShopifyRpcClient =
  createRedoShopifyRpcClient({
    baseURL: new URL("https://shopify-cdn.getredo.com/rpc"),
  });

export async function getAutocheckWithStoreUrl(
  storeURL: string,
  customerID?: string,
  foreignCurrency?: string,
  currencyExchangeRate?: string,
): Promise<WidgetAutocheck> {
  const url = new URL("/widgets/autocheck", "https://shopify-cdn.getredo.com");
  url.searchParams.append("storeUrl", storeURL);
  if (customerID) {
    url.searchParams.append(
      "customer_id",
      customerID.replace("gid://shopify/Customer/", ""),
    );
  }
  if (foreignCurrency) {
    url.searchParams.append("foreign_currency", foreignCurrency);
  }

  const response = await fetch(String(url));
  if (!response.ok) {
    throw response;
  }
  return await response.json();
}

export async function getFeesWithStoreUrl(
  storeURL: string,
): Promise<WidgetFees | { error: string }> {
  const url = new URL("/widgets/fees", "https://shopify-cdn.getredo.com");
  url.searchParams.append("storeUrl", storeURL);

  const response = await fetch(String(url));
  if (!response.ok) {
    throw response;
  }
  return await response.json();
}

/**
 * POST /widgets/:widget_id/selling-plan/:sellingPlanId
 */
export async function getSellingPlanInfo(
  storeURL: string,
  sellingPlanId: number,
): Promise<any> {
  const url = new URL(
    `/widgets/selling-plan/${sellingPlanId}`,
    "https://shopify-cdn.getredo.com",
  );
  url.searchParams.append("storeUrl", storeURL);

  const response = await fetch(String(url));
  if (!response.ok) {
    throw response;
  }
  return await response.json();
}

export async function getCheckoutOptimizationConfigWithStoreUrl(
  storeUrl: string,
): Promise<ManuallyTypedWidgetCheckoutOptimizationConfig> {
  const response = await shopifyRpcClient(
    getCheckoutOptimizationConfig,
    { storeUrl_forCacheRoutingOnly: storeUrl },
    { method: "GET", requestHeaders: { [STORE_URL_HEADER]: storeUrl } },
  );
  return {
    ...response,
    checkoutTreeNode: response.checkoutTreeNode as CheckoutTreeNode,
  };
}

export async function getCheckoutOptimizationCustomerWithStoreUrl({
  storeUrl,
  customerIdentifier,
}: {
  storeUrl: string;
  customerIdentifier:
    | { id: string }
    | { emailAddress: string }
    | { phoneNumber: string };
}): Promise<GetCheckoutOptimizationCustomerOutput> {
  return await shopifyRpcClient(
    getCheckoutOptimizationCustomer,
    { customerIdentifier, storeUrl_forCacheRoutingOnly: storeUrl },
    { method: "GET", requestHeaders: { [STORE_URL_HEADER]: storeUrl } },
  );
}

export async function getPreProtected(
  storeURL: string,
  email: string,
): Promise<{ preProtected: boolean; prePackageProtected: boolean }> {
  const url = new URL(
    "/widgets/pre-protected",
    "https://shopify-cdn.getredo.com",
  );
  url.searchParams.append("storeUrl", storeURL);
  url.searchParams.append("email", email);
  const response = await fetch(String(url));
  if (!response.ok) {
    throw response;
  }
  return await response.json();
}

export async function smsOptInWithStoreUrl(
  storeUrl: string,
  phoneNumber: string,
  optIn: boolean,
): Promise<{ success: boolean; message?: string }> {
  return await shopifyRpcClient(
    smsOptIn,
    { phoneNumber, optIn },
    { method: "POST", requestHeaders: { [STORE_URL_HEADER]: storeUrl } },
  );
}

export async function getOrderEditingSettingsWithStoreUrl(storeUrl: string) {
  return await shopifyRpcClient(
    getOrderEditingSettings,
    {},
    {
      method: "GET",
      requestHeaders: { [STORE_URL_HEADER]: storeUrl },
    },
  );
}
